<!--
  Rechnungen im Kundenbereich.

  Die Rechnung selbst liegt bei Stripe — wir verlinken sie, statt ein eigenes
  PDF zu bauen. Zahlungsmittel ändert der Kunde ebenfalls dort: Kartendaten
  kommen nie in dieses System.
-->
<script lang="ts">
  import { api } from '../lib/api.ts';
  import HilfeLink from '../lib/HilfeLink.svelte';
  import { euro, datum } from '../lib/sitzung.svelte.ts';

  interface Rechnung {
    id: string;
    /**
     * Die Rechnungsnummer aus lexoffice. `null`, solange der Beleg dort noch
     * nicht angelegt ist — dann steht hier bewusst keine Nummer statt der
     * internen Vorgangsnummer (Etappe 20).
     */
    nummer: string | null;
    status: string;
    betrag_brutto: number;
    rechnungsdatum: string;
    faellig_am: string | null;
    bezahlt_am: string | null;
    zeitraum_von: string | null;
    zeitraum_bis: string | null;
    stripe_pdf_url: string | null;
    stripe_ansicht_url: string | null;
    leistung: string | null;
  }

  const STATUSTEXT: Record<string, string> = {
    offen: 'offen',
    bezahlt: 'bezahlt',
    fehlgeschlagen: 'Zahlung hat nicht geklappt',
    storniert: 'storniert',
  };

  let rechnungen = $state<Rechnung[]>([]);
  let fehler = $state<string | null>(null);

  $effect(() => {
    void (async () => {
      try {
        rechnungen = (await api.get<{ rechnungen: Rechnung[] }>('/api/portal/rechnungen')).rechnungen;
        fehler = null;
      } catch (f) {
        fehler = f instanceof Error ? f.message : 'Das konnten wir gerade nicht laden';
      }
    })();
  });

  async function zahlungsmittel(): Promise<void> {
    try {
      const { url } = await api.post<{ url: string }>('/api/portal/zahlungsmittel');
      location.href = url;
    } catch (f) {
      fehler = f instanceof Error ? f.message : 'Das ist gerade nicht möglich';
    }
  }
</script>

<h1>Rechnungen</h1>
<HilfeLink slug="kundenbereich-rechnungen" text="Anleitung: Rechnungen und Verträge ansehen" />

{#if fehler}<p class="fehler">{fehler}</p>{/if}

<div class="karte">
  {#if rechnungen.length === 0}
    <p class="leise">Hier erscheinen Ihre Rechnungen, sobald es welche gibt.</p>
  {:else}
    <ul class="eintraege">
      {#each rechnungen as rechnung (rechnung.id)}
        <li>
          <div class="zeile">
            <strong>{euro(rechnung.betrag_brutto)}</strong>
            <span
              class="marke"
              class:gut={rechnung.status === 'bezahlt'}
              class:schlecht={rechnung.status === 'fehlgeschlagen'}
            >
              {STATUSTEXT[rechnung.status] ?? rechnung.status}
            </span>
          </div>
          <div class="leise klein">
            {#if rechnung.nummer}{rechnung.nummer} · {/if}{datum(rechnung.rechnungsdatum)}
            {#if rechnung.leistung} · {rechnung.leistung}{/if}
            {#if rechnung.zeitraum_von}
              · für {datum(rechnung.zeitraum_von)} bis {datum(rechnung.zeitraum_bis)}
            {/if}
          </div>
          {#if rechnung.status === 'fehlgeschlagen'}
            <p class="klein">
              Meist liegt es an einer abgelaufenen Karte. Sie können das unten selbst ändern — oder
              rufen Sie uns an. <strong>Ihre Leistungen laufen zunächst weiter.</strong>
            </p>
          {/if}
          {#if rechnung.stripe_ansicht_url || rechnung.stripe_pdf_url}
            <p class="klein">
              <a href={rechnung.stripe_pdf_url ?? rechnung.stripe_ansicht_url} target="_blank" rel="noreferrer">
                Rechnung ansehen und herunterladen
              </a>
            </p>
          {/if}
        </li>
      {/each}
    </ul>
  {/if}

  <div class="knopfreihe">
    <button type="button" class="sekundaer" onclick={zahlungsmittel}>Zahlungsmittel ändern</button>
  </div>
  <p class="leise klein">
    Dafür öffnet sich die Seite unseres Zahlungsdienstleisters. Ihre Kartendaten liegen dort und
    niemals bei uns.
  </p>
</div>
